import React from 'react';
import { Icon } from './Icon.jsx';

const VARIANTS = {
  primary:   { bg: 'var(--primary)', color: 'var(--on-primary)', border: 'transparent' },
  secondary: { bg: 'var(--accent-blue)', color: '#fff', border: 'transparent' },
  outline:   { bg: 'transparent', color: 'var(--text-body)', border: 'var(--border-strong)' },
  ghost:     { bg: 'transparent', color: 'var(--text-body)', border: 'transparent' },
  danger:    { bg: 'var(--status-overdue)', color: '#fff', border: 'transparent' },
};

const SIZES = {
  lg: { height: 'var(--control-h)', padding: '0 24px', font: 'var(--fs-h3)', gap: 10, icon: 20 },
  md: { height: 48, padding: '0 20px', font: 'var(--fs-body)', gap: 8, icon: 18 },
  sm: { height: 40, padding: '0 14px', font: 'var(--fs-sm)', gap: 6, icon: 16 },
};

/**
 * Button — GetPay93 action button. Primary = brand green, white extrabold label,
 * 12px radius, 56px tall at lg. Hover darkens; press shrinks slightly.
 */
export function Button({
  variant = 'primary',
  size = 'lg',
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled = false,
  children,
  style = {},
  ...rest
}) {
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const v = VARIANTS[variant] || VARIANTS.primary;
  const s = SIZES[size] || SIZES.lg;

  let bg = v.bg;
  if (!disabled && hover) {
    if (variant === 'primary') bg = 'var(--primary-hover)';
    else if (variant === 'secondary') bg = 'var(--accent-blue-hover)';
    else if (variant === 'outline' || variant === 'ghost') bg = 'rgba(148,163,184,0.10)';
    else if (variant === 'danger') bg = '#DC2E2E';
  }

  const styles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: s.gap,
    height: s.height,
    padding: s.padding,
    width: fullWidth ? '100%' : undefined,
    background: bg,
    color: v.color,
    border: `1.5px solid ${v.border}`,
    borderRadius: 'var(--radius)',
    fontFamily: 'var(--font-sans)',
    fontWeight: 'var(--fw-extrabold)',
    fontSize: s.font,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transform: active && !disabled ? 'scale(0.97)' : 'scale(1)',
    transition: 'background .15s ease, transform .1s ease, opacity .15s ease',
    whiteSpace: 'nowrap',
    ...style,
  };

  return (
    <button
      style={styles}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      {...rest}
    >
      {iconLeft && <Icon name={iconLeft} size={s.icon} strokeWidth={2.4} />}
      {children}
      {iconRight && <Icon name={iconRight} size={s.icon} strokeWidth={2.4} />}
    </button>
  );
}
